import { Option } from '../components/Elements/Dropdown'
import { Date as DateType, CategoryGroup, Priority, ReportDashboardFieldFilter, Status } from '../types'
import { getUnixTime, subHours } from 'date-fns'
import {
  trans, getLocale, CUSTOM_FILTER_HOURS, FILTER_ALL, TIMELINE_CUSTOM_HOURS, TIMELINE_CUSTOM,
  WEATHER_FAHRENHEIT, WEATHER_CELSIUS, TIMELINE_DAYS, TIMELINE_HOURS, STATUS_GROUP_ACTIVE, STATUS_GROUP_ALL, STATUS_GROUP_INACTIVE, COMPLETE
} from '../translator'
import formatDistance from 'date-fns/formatDistance'
import parseISO from 'date-fns/parseISO'
import * as dateFnLocales from 'date-fns/locale'

export const IssuesStatusOptions: Option[] = [
  { value: 'all', label: trans(STATUS_GROUP_ALL, {}, 'dashboard') },
  { value: 'active', label: trans(STATUS_GROUP_ACTIVE, {}, 'dashboard') },
  { value: 'inactive', label: trans(STATUS_GROUP_INACTIVE, {}, 'dashboard') }
]

export const ChecklistStatusOptions: Option[] = [
  { value: 'all', label: trans(STATUS_GROUP_ALL, {}, 'dashboard') },
  { value: 'active', label: trans(STATUS_GROUP_ACTIVE, {}, 'dashboard') },
  { value: 'complete', label: trans(COMPLETE, {}, 'dashboard') }
]

export const TimelineOptions: Option[] = [
  { value: 'all', label: trans(FILTER_ALL, {}, 'dashboard') },
  { value: '1', label: trans(TIMELINE_CUSTOM_HOURS, { '%count%': 1 }, 'dashboard') },
  { value: '3', label: trans(TIMELINE_CUSTOM_HOURS, { '%count%': 3 }, 'dashboard') },
  { value: '6', label: trans(TIMELINE_CUSTOM_HOURS, { '%count%': 6 }, 'dashboard') },
  { value: '12', label: trans(TIMELINE_CUSTOM_HOURS, { '%count%': 12 }, 'dashboard') },
  { value: '24', label: trans(TIMELINE_CUSTOM_HOURS, { '%count%': 24 }, 'dashboard') },
  { value: 'custom', label: trans(TIMELINE_CUSTOM, {}, 'dashboard') }
]

export const WeatherTempUnits: Option[] = [
  { value: 'celsius', label: trans(WEATHER_CELSIUS, {}, 'dashboard') },
  { value: 'farenheit', label: trans(WEATHER_FAHRENHEIT, {}, 'dashboard') }
]

/**
 * Map Issues Status from the Dropdown Option to the Issues Status Array
 */
export const setPriorityFilters = (status: Option, setFilterStatuses: any) => {
  switch (status) {
    case IssuesStatusOptions[0]:
      return setFilterStatuses(IssuesStatusOptions[0].value)
    case IssuesStatusOptions[1]:
      return setFilterStatuses(IssuesStatusOptions[1].value)
    case IssuesStatusOptions[2]:
      return setFilterStatuses(IssuesStatusOptions[2].value)
    default:
      return setFilterStatuses(IssuesStatusOptions[0].value)
  }
}

export const findStatusGroupOption = (status: string|null) => {
  switch (status) {
    case 'all':
      return IssuesStatusOptions[0]
    case 'active':
      return IssuesStatusOptions[1]
    case 'inactive':
      return IssuesStatusOptions[2]
    default:
      return IssuesStatusOptions[0]
  }
}

export const findChecklistStatusOption = (status: string|null) => {
  switch (status) {
    case 'active':
      return ChecklistStatusOptions[1]
    case 'complete':
      return ChecklistStatusOptions[2]
    default:
      return ChecklistStatusOptions[0]
  }
}

export const findActiveStatuses = (statuses: Status[]) => {
  return statuses.filter((status:Status) => !status.is_closing_status) || []
}

export const findCompleteStatuses = (statuses: Status[]) => {
  return statuses.filter((status:Status) => status.is_closing_status) || []
}

export const DoughnutChartColors = [
  '#d7f2f3',
  '#6D8794',
  '#82E4CD',
  '#F2A2B3',
  '#1F7ED6',
  '#FFBB38',
  '#F89F83',
  '#FE90E6',
  '#103F6B',
  '#5DC389',
  '#82DECD',
  '#E79B42',
  '#8FBFEB',
  '#4968D3',
  '#A3DDBC',
  '#31D331',
  '#A831D3',
  '#972038',
  '#849720',
  '#85327A',
  '#856432',
  '#E7DB8D',
  '#D35131',
  '#2525AD',
  '#A6E774',
  '#D33182',
  '#20972C',
  '#B7A0EE',
  '#D3D331',
  '#32856F'
]

export const ChartColorsVariation1 = [
  '#2A9D8F', // Muted Jade
  '#264653', // Deep Teal
  '#8AB17D', // Sage Green
  '#E9C46A', // Warm Sand
  '#F4A261', // Burnt Sienna
  '#E76F51', // Terracotta
  '#6D597A', // Dusty Violet
  '#B56576', // Rosewood
  '#355070', // Slate Blue
  '#7F9183', // Warm Grey-Green
  '#A5A58D', // Olive Stone
  '#CB997E', // Soft Clay
  '#D4A373', // Golden Ochre
  '#936639', // Earth Brown
  '#495057', // Charcoal Grey
  '#4040BF', // Indigo Blue
  '#40BF40', // Grass Green
  '#BF40AA', // Orchid
  '#5371C6', // Cornflower Blue
  '#66A3CC', // Sky Blue
  '#A2BF40', // Chartreuse
  '#643181', // Royal Purple
  '#318131', // Pine Green
  '#B175BD', // Lilac
  '#86862D', // Olive Gold
  '#53C67D', // Mint Green
  '#862D2D', // Brick Red
  '#862D62', // Wine
  '#4F7043', // Moss Green
  '#704643' // Coffee Brown
]

// export const ChartColorsVariation2 = [
//   '#32325D', // Deep Indigo
//   '#2E2E55',
//   '#2A2A4E',
//   '#262647',
//   '#222240',
//   '#1E1E39',
//   '#1A1A32',
//   '#16162B',
//   '#121224',
//   '#0E0E1D',
//   '#0A0A1A',
//   '#080818',
//   '#060616',
//   '#040414',
//   '#030320' // Almost Black Navy
// ]

export const ChartColorsVariation2 = [
  '#525f7f',
  '#566382',
  '#596684',
  '#5d6a87',
  '#606e8a',
  '#64718c',
  '#67758f',
  '#6b7892',
  '#6e7c94',
  '#728097',
  '#75839a',
  '#79879c',
  '#7c8a9f',
  '#808ea2',
  '#8492a4',
  '#8795a7',
  '#8b99aa',
  '#8e9dac',
  '#92a0af',
  '#95a4b2',
  '#99a8b4',
  '#9cabb7',
  '#a0afba',
  '#a3b2bc',
  '#a7b6bf'
]

export const EventMarkerColours = [
  '#FEB56A', '#FA4B43', '#2FE0CB', '#D568FC', '#6C8BBC',
  '#FF6A35', '#06E172', '#5450C5', '#2196F3', '#FF5722',
  '#8BC34A', '#FF9800', '#9C27B0', '#00BCD4', '#795548',
  '#607D8B', '#E91E63', '#CDDC39', '#009688', '#FF6F00'
]

// lookup table for palettes
export const palettes: Record<string, string[]> = {
  original: DoughnutChartColors,
  variation1: ChartColorsVariation1,
  variation2: ChartColorsVariation2,
  eventMarkers: EventMarkerColours
}

/**
 * Compute a colour for a given data index.
 * Defaults to the Original palette, but can accept any custom palette.
 */
export const computeColor = (
  dataIndex: number,
  paletteName: keyof typeof palettes = 'original'
): string => {
  const palette = palettes[paletteName]
  const colorIndex = dataIndex % palette.length
  return palette[colorIndex]
}

// Alphabetical sort ensures both the timeline and issues widgets assign the same index to the
// same name even when their datasets differ (e.g. markers with zero issues are absent from one).
export const buildColorMapByName = (names: string[]): Map<string, string> => {
  const sorted = [...new Set(names)].sort()
  return new Map(sorted.map((name, i) => [name, computeColor(i, 'eventMarkers')]))
}

/**
 * Return a legible label colour (white or translucent black) based on the background colour behind the label
 */
export const getContrastLabelColor = (hexColor: string): string => {
  if (!hexColor) return 'rgba(0, 0, 0, 0.6)' // default to semi-transparent black

  const hex = hexColor.replace(/^#/, '')
  const r = parseInt(hex.substring(0, 2), 16)
  const g = parseInt(hex.substring(2, 4), 16)
  const b = parseInt(hex.substring(4, 6), 16)

  const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255

  return luminance > 0.6
    ? 'rgba(0, 0, 0, 0.7)'
    : 'rgba(255, 255, 255, 1)'
}

// Utility: generate neat whole-number ticks for a linear axis.
// Aims for ~6 labels between 0 and maxValue, and avoids labels that are visually on top of each other near the top of the scale.
// Example outputs: maxValue = 2  [0, 1, 2], maxValue = 10 [0, 2, 4, 6, 8, 10], maxValue = 100 [0, 20, 40, 60, 80, 100], maxValue = 101 [0, 20, 40, 60, 80, 101]  (last tick snapped to max)
// Ensures that Nivo bar charts do not use decimal tick values or repeat rounded integer tick values
export const getIntegerTicks = (maxValue: number): number[] => {
  if (!Number.isFinite(maxValue) || maxValue <= 0) return [0]

  const targetTickCount = 6
  const roughStep = maxValue / targetTickCount

  // Simple, user friendly step sizes
  const stepOptions = [1, 2, 5, 10, 20, 50, 100, 200, 500]

  // Pick the smallest step that is big enough to give us roughly the targetTickCount number of ticks.
  // Example: if roughStep = 14.5, choose 20 (the first step that is >= 14.5).
  // If none of the options is large enough, fall back to the largest option.
  const chosenStep =
    stepOptions.find((step) => step >= roughStep) ?? stepOptions[stepOptions.length - 1]

  const ticks: number[] = []
  // Build ticks by walking forward in equal jumps starting at 0.
  // Example: chosenStep = 20, maxValue = 100, steps will be 0, 20, 40, 60, 80, 100
  for (let value = 0; value <= maxValue; value += chosenStep) {
    ticks.push(Math.round(value))
  }

  const roundedMax = Math.round(maxValue)
  const lastTick = ticks[ticks.length - 1]

  // If the last tick already lands exactly on max then the scale is ready
  if (lastTick === roundedMax) {
    return ticks
  }

  // If the last tick is very close to the max then the scale will look odd and be too compressed
  // Decide whether to: (a) replace the last tick with max, or (b) add max as an extra tick. We treat ticks as "too close" if the gap between the last tick and max is very small compared to the overall range.
  const gap = roundedMax - lastTick

  // If gap is zero or negative, avoid adding another tick.
  if (gap <= 0) {
    return ticks
  }

  const range = roundedMax
  const relativeGap = gap / range // e.g. 3 / 100 = 0.03 (3%)
  const closeThreshold = 0.1 // 10%

  // If the last tick and max are very close (e.g. 97 and 100 on a 0–100 scale),
  // snap the last tick up to max instead of adding another label.
  if (relativeGap < closeThreshold) {
    ticks[ticks.length - 1] = roundedMax
    return ticks
  }

  // Otherwise, add max as an additional tick so it is clearly visible.
  ticks.push(roundedMax)
  return ticks
}

export const transformFilter = (filter: {type:string, status?:{value, label}, logs:[], priorities?:[], categoryGroups?:[], resolutions?:{value: string, label: string}[], hideCategoriesWithNoIssues?: boolean, fromDate: number, toDate: number, eventMarkers?: {value: string, label: string}[] }, prioritiesData: Priority[], categoryGroupsData: CategoryGroup[]) => {
  const hasValidDateRange = Number.isFinite(filter.fromDate) && Number.isFinite(filter.toDate)
  const diffInSeconds = hasValidDateRange ? filter.toDate - filter.fromDate : NaN
  const diffInHours = diffInSeconds / 3600
  const ceilDiffInHours = Math.ceil(diffInHours)
  const predefinedTimelineObject = hasValidDateRange
    ? {
        value: ceilDiffInHours.toString(),
        label: ceilDiffInHours >= 48
          ? trans(TIMELINE_DAYS, { '%count%': Math.ceil(ceilDiffInHours / 24) }, 'dashboard')
          : trans(TIMELINE_HOURS, { '%count%': ceilDiffInHours }, 'dashboard')
      }
    : TimelineOptions[0]

  const transformedFilter: ReportDashboardFieldFilter = {
    activeLogsId: [],
    statuses: [], // this will actually be used for status
    timeline: predefinedTimelineObject,
    customTimeline: undefined,
    selectedPriorities: [],
    selectedCategoryGroups: [],
    selectedCategoryGroup: undefined,
    selectedEventMarkers: [],
    hideEmpty: false,
    fromDate: undefined,
    toDate: undefined,
    statusGroup: 'all' // to satisfy interface, will not be used
  }

  Object.keys(filter).forEach(key => {
    switch (key) {
      case 'status':
        transformedFilter.statuses = []
        transformedFilter.statuses.push(filter.status.value)
        break
      case 'categories':
        // @ts-ignore
        transformedFilter.selectedCategoryGroup = categoryGroupsData.find(availableCategory => filter.categories.some(selectedCategory => selectedCategory.value === availableCategory.id))
        break
      case 'categoryGroups':
        // @ts-ignore
        transformedFilter.selectedCategoryGroups = filter.type === 'issueByCategoryGroup' && filter.categoryGroups.length === 0
          ? categoryGroupsData
        // @ts-ignore
          : filter.categoryGroups.map(selectedCategoryGroup => categoryGroupsData.find(availableCategory => selectedCategoryGroup.value === availableCategory.id))
        break
      case 'hideCategoriesWithNoIssues':
        transformedFilter.hideEmpty = !filter.hideCategoriesWithNoIssues
        break
      case 'priorities':
        // @ts-ignore
        transformedFilter.selectedPriorities = filter.priorities.map(selectedPriority => prioritiesData.find(availablePriority => selectedPriority.value === availablePriority.id))
        break
      case 'logs':
        transformedFilter.activeLogsId = filter.logs.map((log: any) => log.value)
        break
      case 'fromDate':
        transformedFilter.fromDate = filter.fromDate
        break
      case 'toDate':
        transformedFilter.toDate = filter.toDate
        break
      case 'eventMarkers':
        transformedFilter.selectedEventMarkers = filter.eventMarkers.map(marker => ({ id: marker.value, name: marker.label }))
        break
      case 'resolutions':
        // @ts-ignore
        transformedFilter.selectedResolutions = filter.resolutions.map(r => ({ id: r.value, name: r.label }))
        break
    }
  })
  return transformedFilter
}

export const getTextForTimelineFilterInfo = (filter:any) => {
  const translatedTimelineLabel = TimelineOptions.find((option) => option.value === filter.timeline.value)?.label
  return translatedTimelineLabel ?? (filter.customTimeline
    ? trans(CUSTOM_FILTER_HOURS, {
      '%duration%': filter.customTimeline
        ? filter?.customTimeline
        : filter?.timeline?.value
    }, 'dashboard')
    : trans(FILTER_ALL, {}, 'dashboard'))
}

export const fromDateFallbackValue = (customValue?:string, timelineValue?:string, earliestSelectedLogTimestamp?:number) => {
  return ((customValue || timelineValue) && timelineValue !== 'all')
    ? getUnixTime(
      subHours(
        new Date(),
        customValue ? parseInt(customValue) : parseInt(timelineValue)
      )
    )
    : earliestSelectedLogTimestamp !== undefined ? earliestSelectedLogTimestamp : getUnixTime(new Date()) - 360000
}

export const mapDateFormatToDayMonth = (dateFormat: string): string => {
  switch (dateFormat) {
    case 'dd/mm/yyyy':
      return 'dd/MM'
    case 'mm/dd/yyyy':
      return 'MM/dd'
    case 'yyyy-mm-dd':
      return 'MM-dd'
    default:
      return 'dd/MM'
  }
}

export const getTimeFormat = (isUseAmPmString: string | boolean, isUseTimezone: boolean = false): string => {
  const isUseAmPm = isUseAmPmString === '1' || isUseAmPmString === true

  let timeFormat = isUseAmPm ? 'hh:mm a' : 'HH:mm'

  if (isUseTimezone) {
    timeFormat += ' zzz'
  }

  return timeFormat
}

export const getTimestampFormat = (isUseAmPmString: string | boolean, isUseTimezone: boolean = false): string => {
  const isUseAmPm = isUseAmPmString === '1' || isUseAmPmString === true

  let timeFormat = isUseAmPm ? 'hh:mm:ss a' : 'HH:mm:ss'

  if (isUseTimezone) {
    timeFormat += ' zzz'
  }

  return timeFormat
}

export const getNormalizedAngle = (angle: number) => {
  let normalizedAngle = angle % (Math.PI * 2)
  if (normalizedAngle < 0) {
    normalizedAngle += Math.PI * 2
  }

  return normalizedAngle
}

export const getTimeAgo = (created_at: DateType) => {
  const localeString = getLocale().slice(0, 2)
  const locale = dateFnLocales[localeString] ?? dateFnLocales.enUS
  return formatDistance(parseISO(created_at.date), new Date(), {
    addSuffix: true,
    locale
  })
}

export const nl2br = (value: string = ''): string => {
  const escaped = String(value).replace(/[&<>"']/g, (char) => {
    const entities = {
      '&': '&amp;',
      '<': '&lt;',
      '>': '&gt;',
      '"': '&quot;',
      "'": '&#39;'
    }
    return entities[char]
  })

  return escaped.replace(/\r\n|\n\r|\r|\n/g, '<br />')
}
